/**
 * EditMarketingModal — edit marketing attribution fields for a job.
 *
 * Allows direct editing of the job's `marketing` metadata, including
 * campaignName, leadSource, callCenterRep, appointmentDate, saleOutcome,
 * aggregatorPlatform, and leadCost.
 *
 * Changes are persisted via JobService.update() and reflected in the
 * AppDataContext jobs array on success.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { JobService } from "@/services/job.service";
import type { Job, MarketingAttribution } from "@/data/types";

export interface EditMarketingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  onSaved?: (updatedJob: Job) => void;
}

const SALE_OUTCOMES = [
  "Sold",
  "Lost",
  "Pending",
  "No Decision",
  "Cancelled",
] as const;

export function EditMarketingModal({
  open,
  onOpenChange,
  job,
  onSaved,
}: EditMarketingModalProps) {
  const [form, setForm] = useState<MarketingAttribution>({
    leadSource: "",
    campaignName: "",
    aggregatorPlatform: "",
    callCenterRep: "",
    appointmentDate: "",
    saleOutcome: "",
    leadCost: undefined,
    campaign: "",
  });

  useEffect(() => {
    if (open) {
      const m = job.marketing || {
        leadSource: "",
        campaignName: "",
        aggregatorPlatform: "",
        callCenterRep: "",
        appointmentDate: "",
        saleOutcome: "",
        leadCost: undefined,
        campaign: "",
      };
      setForm(m);
    }
  }, [open, job]);

  const handleSave = async () => {
    if (!form.leadSource.trim()) {
      toast.error("Lead Source is required");
      return;
    }
    if (!form.campaignName.trim()) {
      toast.error("Campaign Name is required");
      return;
    }

    try {
      const { data: updated, error } = await JobService.update(job.id, {
        marketing: { ...form },
      });

      if (error || !updated) {
        toast.error("Failed to update marketing data");
        return;
      }

      toast.success("Marketing data updated");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (err) {
      console.error("Update marketing error:", err);
      toast.error("Unexpected error saving marketing data");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Marketing Attribution
          </DialogTitle>
          <DialogDescription>
            Update campaign and lead source details for this job.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="leadSource">Lead Source *</Label>
              <Input
                id="leadSource"
                value={form.leadSource}
                onChange={(e) => setForm({ ...form, leadSource: e.target.value })}
                placeholder="e.g. HomeAdvisor, Referral"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaignName">Campaign Name *</Label>
              <Input
                id="campaignName"
                value={form.campaignName}
                onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
                placeholder="e.g. Q1 2026 Push"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="aggregatorPlatform">Aggregator Platform</Label>
              <Input
                id="aggregatorPlatform"
                value={form.aggregatorPlatform}
                onChange={(e) => setForm({ ...form, aggregatorPlatform: e.target.value })}
                placeholder="e.g. Angi, Thumbtack"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="callCenterRep">Call Center Rep</Label>
              <Input
                id="callCenterRep"
                value={form.callCenterRep}
                onChange={(e) => setForm({ ...form, callCenterRep: e.target.value })}
                placeholder="Rep name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="appointmentDate">Appointment Date</Label>
              <Input
                id="appointmentDate"
                type="date"
                value={form.appointmentDate}
                onChange={(e) => setForm({ ...form, appointmentDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="saleOutcome">Sale Outcome</Label>
              <Select
                value={form.saleOutcome}
                onValueChange={(v) => setForm({ ...form, saleOutcome: v })}
              >
                <SelectTrigger id="saleOutcome">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  {SALE_OUTCOMES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="leadCost">Lead Cost ($)</Label>
            <Input
              id="leadCost"
              type="number"
              step="0.01"
              min="0"
              value={form.leadCost ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  leadCost: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              placeholder="0.00"
            />
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
